import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQuery } from "@tanstack/react-query";

import MDBox from "components/MDBox";
import { Success, Error } from "ui-components/Alert";
import { getFormRender, initialValues, adjustmentValidation } from "./adjustment-schema";
import {
  formRender as detailRender,
  initialValues as detailValues,
  adjustmentDetailValidation,
} from "./adjustment-detail-schema";
import AdjustmentInfo from "./components/AdjustmentInfo";
import { inventoryColumns } from "./components/columns";
import MDTypography from "components/MDTypography";
import { StripedDataGrid } from "layouts/views/components/Grid";
import { useCustomerValue } from "providers/CustomerContext";
import { createAdjustment } from "services/data.internal.service";
import AdjustmentDetailInfo from "./components/AdjustmentDetailInfo";
import MDButton from "components/MDButton";
import styled from "@emotion/styled";
import { InboundSaveButton } from "ui-components/Form";
import dayjs from "dayjs";
import { Fieldset } from "ui-components/Form";
import { Card, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { InboundLayout } from "ui-components/Layout";
import { getCommonInventoryList } from "services/data.inventory.service";
import { DeleteButton } from "ui-components/IconButton";
import { AdjustmentCaption } from "layouts/views/inventory/components/Caption";
import { DialogAlert } from "layouts/views/components/Alert";
import { LoadingDialog } from "ui-components/Alert";
import { LoadingAlert } from "ui-components/Alert";
import { Error_ } from "ui-components/Alert";
import ReceivingDivisionGroup from "./components/ReceivingDivisionGroup";
import { getShowCODivision } from "services/data.inbound.service";
import { upload } from "@testing-library/user-event/dist/upload";

const defaultDetailValues = {
  ...detailValues,
  holdPallet: { label: "All pallet", value: "A" },
};

const EditableStyledDataGrid = styled(StripedDataGrid)({
  ".custom-editable-header": {
    backgroundColor: "#1A73E8 !important",
    color: "#fff",
    fontWeight: "bold",
    whiteSpace: "normal !important",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal !important",
    lineHeight: 1,
  },
  ".MuiDataGrid-columnHeader": {
    backgroundColor: "#f0f2f5",
  },
  ".MuiDataGrid-cell": {
    whiteSpace: "normal !important",
    "word-wrap": "break-word !important",
  },
  ".right-align-header": {
    textAlign: "right",
  },
  ".highlight": {
    backgroundColor: "pink !important",
  },
});

function AddAdjustment() {
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const { customerCode } = useCustomerValue();
  const errorColumns = [
    {
      field: "id",
      headerName: "LocAvaiID",
      sortable: false,
      align: "center",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "locationID",
      headerName: "Location ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "skuCode",
      headerName: "SKU Code",
      sortable: false,
      align: "left",
      flex: 1.8,
      minWidth: 180,
    },
    {
      field: "palletNbr",
      headerName: "Pallet Nbr",
      sortable: false,
      align: "left",
      flex: 1.2,
      minWidth: 112,
    },
    {
      field: "cartonID",
      headerName: "Carton ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "mln",
      headerName: "MLN",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "message",
      headerName: "Message",
      sortable: false,
      align: "left",
      flex: 1.6,
      minWidth: 160,
    },
  ];

  const [detail, setDetail] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loadEnabled, setLoadEnabled] = useState(false);
  //const [details, setDetails] = useState([]);
  const [errorItems, setErrorItems] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const [uploadData, setUploadData] = useState([]);
  const [showDivision, setShowDivision] = useState(false);

  const createAdjustmentMutation = useMutation(createAdjustment, {
    onSuccess: (data) => {
      //console.log("final submit button function");
      setDialog(null);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Success data={data} onChange={setAlert} />);
      setUploadData([]);
    },
    onError: (data) => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

      setDialog(null);
      const errorData = data.response.data.data;
      if (errorData) {
        setDialog(
          <Dialog open={true} maxWidth={"lg"}>
            <DialogTitle>{data.response.data.message}</DialogTitle>
            <DialogContent>
              <EditableStyledDataGrid
                columns={errorColumns}
                rows={errorData}
                sx={{
                  borderRadius: "0.75rem",
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                getRowHeight={() => "auto"}
                hideFooterSelectedRowCount={true}
              />
            </DialogContent>

            <DialogActions style={{ display: "flex", justifyContent: "center" }}>
              <MDButton
                color="info"
                style={{ width: "4rem" }}
                onClick={() => {
                  setDialog(null);
                }}
              >
                Ok
              </MDButton>
            </DialogActions>
          </Dialog>
        );
        const remainingDetail = detail.filter((row) => !errorData.some((err) => err.id === row.id));
        setDetail(remainingDetail);

        setErrorItems(errorData);
      }
      setLoadEnabled(true);
      setAlert(<Error data={data} onChange={setAlert} />);
    },
  });

  const defaultValues = {
    ...initialValues,
    customerCode,
    adjustmentDate: dayjs(new Date()).format("YYYY-MM-DD"),
  };

  const form = useForm({
    defaultValues: defaultValues,
    values: defaultValues,
    resolver: yupResolver(adjustmentValidation),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    trigger,
    reset,
    setValue,
  } = form;

  const [warehouseCode] = watch(["warehouseCode"]);
  const divReqValue = watch("divReq") ?? true;

  useEffect(() => {
    setDetail([]);
    setInventory([]);
  }, [warehouseCode]);

//    useEffect(() => {
//    console.log("Switch changed:", divReqValue);
//  }, [divReqValue]);

  useQuery({
    queryKey: ["show-division", customerCode],
    //queryFn: () => getShowDivision({ customerCode }),
    queryFn: () => {
    //console.log("Calling getShowDivision with:", { customerCode });
    return getShowCODivision({ customerCode });
  },
    enabled: Boolean(customerCode),
    //onSuccess: (data) => setShowDivision(data.data.data),
    onSuccess: (data) => {
    //console.log("API response:", data);
    //console.log("API response data.data.data:", data.data.data);
    setShowDivision(data.data.data);
  },
    onError,
  });

  useEffect(() => {
  console.log("showDivision state:", showDivision);
}, [showDivision]);

  const formRender = getFormRender(showDivision);
  const infoProps = {
    control,
    errors,
    formRender,
    customerCode,
    setValue,
  };

  const handleClose = () => {
    setDialog(null);
  };

  const onSubmit = async () => {
    //here should add checking whether gt upload file or not.
    //validation should add here
    //console.log("submit button clicked");
    //console.log("showDivision",showDivision);
    
    //if (showdivision == true)  certain value, need check the file must exist.
    //if (showDivision == true){
      //console.log("detail",detail);
      //console.log("uploadData",uploadData);
      //console.log("setUploadData",setUploadData);
    //}
    //console.log("divReqValue from create adjustment",divReqValue)
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }

    setDialog(<LoadingDialog />);

    const { ...data } = getValues();

    const req = {
      ...data,
      divisionQuantity: JSON.stringify(uploadData),
      divReq: divReqValue,
      adjustmentDetails: detail.map((x) => {
        const { expiryDate, ...data } = x;
        return {
          expiryDate: expiryDate ? dayjs(expiryDate, "DD / MM / YYYY").format("YYYY-MM-DD") : null,
          ...data,
        };
      }),
    };

    //console.log(req,"from final");

    createAdjustmentMutation.mutate(req, {
      onSuccess: () => {
        reset();
      },
    });
  };

  const renderDialog = () => {
    const skus = detail
      .filter((x) => x.adjustedTotal === 0)
      .map((x) => {
        return `${x.skuCode}(${x.palletNbr})`;
      })
      .join(", ");
    setDialog(
      <DialogAlert
        open={true}
        title={"These inventory will be removed"}
        content={skus}
        onClose={handleClose}
        onOk={onSubmit}
      />
    );
  };

  const onSubmitClicked = (req) => {
    if (detail.some((x) => x.adjustedTotal === 0)) {
      renderDialog();
    } else {
      onSubmit();
    }
  };

  const createStatus = createAdjustmentMutation.status === "loading";
  return (
    <InboundLayout lg={12}>
      {alert}
      {dialog}
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <form noValidate>
            <AdjustmentInfo {...infoProps} />
          </form>
        </MDBox>
      </Card>
      <MDBox sx={{ margin: "1rem 0" }} />
      <AdjustmentDetail
        customerCode={customerCode}
        warehouseCode={warehouseCode}
        inventory={inventory}
        setInventory={setInventory}
        detail={detail}
        setDetail={setDetail}
        loadEnabled={loadEnabled}
        setLoadEnabled={setLoadEnabled}
        errorItems={errorItems}
        setErrorItems={setErrorItems}
        showDivision={showDivision}
        uploadData={uploadData}
        setUploadData={setUploadData}
        divReqValue={divReqValue}
      />
      <InboundSaveButton
        label="submit"
        disabled={createStatus || detail.length === 0}
        url="/inventory/adjustment"
        onClick={onSubmitClicked}
      />
    </InboundLayout>
  );
}

const AdjustmentDetail = ({
  customerCode,
  warehouseCode,
  inventory,
  setInventory,
  detail,
  setDetail,
  loadEnabled,
  setLoadEnabled,
  errorItems,
  setErrorItems,
  showDivision,
  uploadData,
  setUploadData,
  divReqValue
}) => {
  const [alert, setAlert] = useState(null);
  //console.log(divReqValue,"from adjustmentdetails");

  const handleProcessRowUpdateError = (error) => {
    setAlert(<Error content={error.message} onChange={setAlert} />);
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <InventoryGrid
          customerCode={customerCode}
          warehouseCode={warehouseCode}
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          setAlert={setAlert}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
          loadEnabled={loadEnabled}
          setLoadEnabled={setLoadEnabled}
          errorItems={errorItems}
          setErrorItems={setErrorItems}
        />
        {alert}
        <PreviewGrid
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          //details={details}
          uploadData={uploadData}
          setUploadData={setUploadData}
          showDivision={showDivision}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
          divReqValue={divReqValue}
        />
      </MDBox>
    </Card>
  );
};

const InventoryGrid = ({
  customerCode,
  warehouseCode,
  inventory,
  setInventory,
  detail,
  setDetail,
  setAlert,
  handleProcessRowUpdateError,
  loadEnabled,
  setLoadEnabled,
  errorItems,
  setErrorItems,
}) => {
  const [formValues, setFormValues] = useState({ ...defaultDetailValues });
  const [selectedIds, setSelectedIds] = useState([]);

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(adjustmentDetailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = form;

  useEffect(() => {
    reset();
  }, [warehouseCode]);

  const [zoneID, locationID, vendorCode, skuCode, cartonID, palletNbr, holdPallet] = watch([
    "zoneID",
    "locationID",
    "vendorCode",
    "skuCode",
    "cartonID",
    "palletNbr",
    "holdPallet",
  ]);

  const detailProps = {
    control,
    errors,
    formRender: detailRender,
    watch,
    warehouseCode,
    //uploadData,
    //setUploadData,
  };

  const inventoryReq = {
    warehouseCode,
    customerCode,
    zoneID,
    locationID,
    vendorCode,
    skuCode,
    cartonID,
    palletNbr,
    holdPallet: holdPallet.value,
    type: "Adjustment",
  };

  const { isFetching } = useQuery({
    queryKey: [
      "inventory-list",
      warehouseCode,
      customerCode,
      zoneID,
      locationID,
      vendorCode,
      skuCode,
      cartonID,
      palletNbr,
      holdPallet,
      loadEnabled,
    ],
    queryFn: () => getCommonInventoryList(inventoryReq),
    enabled: loadEnabled,
    onSuccess: (data) => {
      const list = data.data.data;
      if (list.length === 0) {
        setAlert(
          <Error
            content="No pallets found, might be occupied by Physical Count CCFreeze, Replenishment, Putaway"
            onChange={setAlert}
          />
        );
        setInventory([]);
      } else {
        const inventoryList = list
          .map((x) => ({
            ...x,
            adjustQuantity: null,
            adjustedQuantity: x.quantityAvailable,
            adjustedTotal: x.quantity,
          }))
          .filter((x) => !detail.some((y) => x.id === y.id));
        setInventory(inventoryList);
      }
      setLoadEnabled(false);
    },
  });

  const onSubmit = () => {
    setLoadEnabled(true);
  };

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (JSON.stringify(updatedRow) !== JSON.stringify(originalRow)) {
      if (!selectedIds.includes(updatedRow.id)) {
        setSelectedIds([...selectedIds, updatedRow.id]);
      }
      const newInventory = inventory.map((inv) => {
        if (inv.id === updatedRow.id) {
          if (updatedRow.adjustQuantity !== null && updatedRow.adjustQuantity != 0) {
            const sum =
              parseInt(updatedRow.quantityAvailable) + parseInt(updatedRow.adjustQuantity);
            if (sum >= 0) {
              originalRow.adjustedQuantity = sum;
              originalRow.adjustedTotal = inv.quantity + parseInt(updatedRow.adjustQuantity);
              originalRow.adjustQuantity = updatedRow.adjustQuantity;
              inv.adjustedQuantity = sum;
              inv.adjustedTotal = inv.quantity + parseInt(updatedRow.adjustQuantity);
              inv.adjustQuantity = updatedRow.adjustQuantity;
            } else {
              originalRow.adjustQuantity = parseInt(inv.quantityAvailable) * -1;
              originalRow.adjustedQuantity = 0;
              originalRow.adjustedTotal = inv.quantity - inv.quantityAvailable;
              inv.adjustQuantity = parseInt(inv.quantityAvailable) * -1;
              inv.adjustedQuantity = 0;
              inv.adjustedTotal = inv.quantity - inv.quantityAvailable;
            }
          } else {
            originalRow.adjustedQuantity = parseInt(inv.quantityAvailable);
            originalRow.adjustedTotal = parseInt(inv.quantity);
            originalRow.adjustQuantity = null;
            inv.adjustedQuantity = parseInt(inv.quantityAvailable);
            inv.adjustedTotal = parseInt(inv.quantity);
            inv.adjustQuantity = null;
          }
        }
        return inv;
      });
      setInventory(newInventory);
    }
    return originalRow;
  };

  const saveDetail = () => {
    const newDetail = inventory.filter((x) => x.adjustQuantity !== null && x.adjustQuantity != 0);
    const unsuccessfulDetail = inventory.filter((x) =>
      selectedIds.some(y => y === x.id) && !newDetail.some(y => y.id === x.id)).map(x => {
        return x.skuCode + ': No Adjust Quantity'
      });
      
      console.log(unsuccessfulDetail)
    if (unsuccessfulDetail.length > 0) {
      const errorMsg = (
        <>
          {unsuccessfulDetail.map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </>
      );
      setAlert(<Error_ content={errorMsg} onChange={setAlert} />);
      if (newDetail.length === 0) {
        return;
      }
    }
    if (detail.length > 0) {
      let modifiedDetail = JSON.parse(JSON.stringify(detail));

      const filteredInv = newDetail.filter((x) => !detail.some((y) => y.id === x.id));

      modifiedDetail = [...modifiedDetail, ...filteredInv];

      setDetail(modifiedDetail);
    } else {
      setDetail(newDetail);
    }
    setErrorItems([]);

    const leftInventory = inventory.filter(
      (x) => x.adjustQuantity === null || x.adjustQuantity === 0
    );
    if (leftInventory.length < inventory.length) {
      setInventory(leftInventory);
    }
  };

  const zerolize = () => {
    const updatedInventory = inventory.map((x) => {
      if (selectedIds.some((id) => id === x.id))
        return { ...x, adjustQuantity: -1 * x.quantityAvailable, adjustedQuantity: 0, adjustedTotal: x.quantity - x.quantityAvailable };
      else return x;
    });
    setInventory(updatedInventory);
  };
  const selectRow = (id) => {
    if (selectedIds.length > id.length) {
      const removed = selectedIds.filter((x) => !id.includes(x));
      let updatedRow = inventory;
      removed.forEach((removedID) => {
        updatedRow = updatedRow.map((x) => {
          if (x.id === removedID) {
            return {
              ...x,
              adjustQuantity: null,
              adjustedQuantity: x.quantityAvailable,
              adjustedTotal: x.quantity,
            };
          } else return x;
        });
      });
      setInventory(updatedRow);
    }
    setSelectedIds(id);
  };

  return (
    <Fieldset label={"Adjustment Detail"}>
      {(isFetching) && <LoadingAlert />}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <AdjustmentDetailInfo {...detailProps} />
        <MDBox mt={2} width="100%" display="flex" justifyContent="center">
          <MDButton
            type="submit"
            variant="gradient"
            color="info"
            disabled={customerCode === null || warehouseCode === null}
          >
            Load
          </MDButton>
        </MDBox>
      </form>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Inventory Datagrid Display
      </MDTypography>
      <div style={{ height: 500, width: "100%" }}>
        <EditableStyledDataGrid
          autoHeight={false}
          columns={inventoryColumns}
          rows={inventory}
          checkboxSelection={true}
          disableRowSelectionOnClick={true}
          onRowSelectionModelChange={selectRow}
          rowSelectionModel={selectedIds}
          sx={{
            borderRadius: "0.75rem",
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          getRowHeight={() => "auto"}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          hideFooterSelectedRowCount={true}
          getRowClassName={(params) => {
            return errorItems.some((x) => x.id === params.row.id) ? "highlight" : "";
          }}
        />
      </div>
      <MDBox mt={2} width="100%" display="flex" justifyContent="center">
        <MDButton
          variant="gradient"
          color="info"
          onClick={zerolize}
          disabled={inventory.length === 0}
        >
          Zerolize
        </MDButton>
        <MDButton
          sx={{ marginLeft: "1.5rem" }}
          variant="gradient"
          color="info"
          onClick={saveDetail}
        >
          Add
        </MDButton>
      </MDBox>
    </Fieldset>
  );
};

const PreviewGrid = ({
  inventory,
  setInventory,
  detail,
  uploadData, setUploadData,
  setDetail,
  showDivision,
  handleProcessRowUpdateError,
  divReqValue
}) => {
  const addBackInventory = (req) => {
    const { adjustQuantity, adjustedQuantity, adjustedTotal, ...data } = req;
    const updatedInventory = [
      ...inventory,
      { adjustQuantity: null, adjustedQuantity: req.quantityAvailable, adjustedTotal: req.quantity, ...data },
    ];
    setInventory(updatedInventory);
  };
  //console.log(divReqValue,"from PreviewGrid");
  const handleDeleteClick = (req) => {
    addBackInventory(req);
    setDetail(detail.filter((row) => row.id !== req.id));
  };
  const previewColumn = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 80,
      cellClassName: "actions",
      getActions: ({ row }) => {
        return [
          <DeleteButton
            title="Delete"
            onClick={() => {
              handleDeleteClick(row);
            }}
          />,
        ];
      },
    },
    ...inventoryColumns,
  ];

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (updatedRow.adjustQuantity === null || updatedRow.adjustQuantity === 0) {
      handleDeleteClick(updatedRow);
      return { ...originalRow, _action: "delete" };
    } else {
      const newDetail = detail.map((det) => {
        if (det.id === updatedRow.id) {
          if (updatedRow.adjustQuantity !== null && updatedRow.adjustQuantity != 0) {
            const sum =
              parseInt(updatedRow.quantityAvailable) + parseInt(updatedRow.adjustQuantity);
            if (sum >= 0) {
              originalRow.adjustedQuantity = sum;
              originalRow.adjustedTotal = det.quantity + parseInt(updatedRow.adjustQuantity);
              originalRow.adjustQuantity = updatedRow.adjustQuantity;
              det.adjustedQuantity = sum;
              det.adjustedTotal = det.quantity + parseInt(updatedRow.adjustQuantity);
              det.adjustQuantity = updatedRow.adjustQuantity;
            } else {
              originalRow.adjustQuantity = parseInt(det.quantityAvailable) * -1;
              originalRow.adjustedQuantity = 0;
              originalRow.adjustedTotal = det.quantity - det.quantityAvailable;
              det.adjustQuantity = parseInt(det.quantityAvailable) * -1;
              det.adjustedQuantity = 0;
              det.adjustedTotal = det.quantity - det.quantityAvailable;
            }
          } else {
            originalRow.adjustedQuantity = parseInt(det.quantityAvailable);
            originalRow.adjustedTotal = det.quantity;
            originalRow.adjustQuantity = null;
            det.adjustedQuantity = parseInt(det.quantityAvailable);
            det.adjustedTotal = det.quantity;
            det.adjustQuantity = null;
          }
        }
        return det;
      });
      setDetail(newDetail);
    }
    return originalRow;
  };

  const caption = () => {
    const totalAdjust =
      detail.length > 0
        ? detail.reduce((total, det) => {
          return total + det.adjustQuantity;
        }, 0)
        : 0;
    return <AdjustmentCaption totalRows={detail.length} totalAdjust={totalAdjust} />;
  };

  return (
    <MDBox p={3}>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Adjustment Detail Preview
      </MDTypography>
      <div style={{ height: 500, width: "100%" }}>
        <EditableStyledDataGrid
          autoHeight={false}
          columns={previewColumn}
          rows={detail}
          sx={{
            borderRadius: "0.75rem",
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          getRowHeight={() => "auto"}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          hideFooterSelectedRowCount={true}
          slots={{
            footer: caption,
          }}
        />
      </div>
      {showDivision === true && (
       <ReceivingDivisionGroup
          details={detail}
          uploadData={uploadData}
          setUploadData={setUploadData}
          divReqValue={divReqValue}
        />
        )}
    </MDBox>
  );
};

export default AddAdjustment;
